const ProblemCard: React.FC<{ title: string; body: string; icon: React.ReactNode }> = ({ title, body, icon }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-700 leading-relaxed">{body}</p>
  </div>
);

interface RoleCardProps {
  label: string;
  headline: string;
  body: string;
  imageUrl: string;
}

const RoleCard: React.FC<RoleCardProps> = ({ label, headline, body, imageUrl }) => (
  <article className="role-card rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden">
    <div className="h-40 overflow-hidden">
      <img src={imageUrl} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="p-8">
      <p className="text-sm uppercase tracking-wide font-semibold text-violet-600 mb-2">
        {label}
      </p>
      <h3 className="text-2xl font-bold text-slate-900 mb-4">{headline}</h3>
      <p className="text-slate-700 leading-relaxed">{body}</p>
    </div>
  </article>
);

const SearchIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const TruthIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ExchangeIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const HandoverIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const GovernanceIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const ContinuityIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

export const OverviewSection: React.FC = () => {
  // All images from Unsplash free license construction category
  const roles: RoleCardProps[] = [
    {
      label: "Project Managers",
      headline: "Keep Projects Moving and Decisions Informed",
      body:
        "ARK gives project managers instant visibility into specs, RFIs, change orders and schedule impacts in one place, freeing up 10+ hours per week and reducing rework across the project.",
      imageUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80"
    },
    {
      label: "Field Teams & Superintendents",
      headline: "On-Site Answers in Seconds",
      body:
        "Field teams use ARK to pull up the latest drawings, installation details and safety requirements from their phone or tablet. Work continues instead of waiting on email replies or office callbacks.",
      imageUrl: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=600&q=80"
    },
    {
      label: "Construction Executives",
      headline: "Protect Margins and Institutional Knowledge",
      body:
        "ARK helps executives reduce risk, standardise best practices and preserve institutional memory as senior staff retire. You get a single, reliable view of how knowledge flows across your portfolio.",
      imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80"
    },
    {
      label: "IT & Knowledge Managers",
      headline: "Secure, Governed AI Your IT Team Can Trust",
      body:
        "With enterprise-grade security, RBAC and controlled integrations, ARK fits cleanly into your existing architecture. Admin dashboards, analytics and usage insights make it easy to govern and scale.",
      imageUrl: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=600&q=80"
    }
  ];

  return (
    <section
      id="overview"
      className="overview-section section-spacing bg-white relative overflow-hidden"
    >
      {/* Decorative construction imagery - Unsplash free license */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-full opacity-5 pointer-events-none hidden xl:block">
        <img
          src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=800&q=80"
          alt="Construction worker reviewing plans"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="container mx-auto relative z-10">
        {/* Overview intro */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
            From fragmented data to operational intelligence
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Project knowledge is scattered across systems, folders, and teams. ARK unifies it into one governed system of intelligence so execution teams can move faster, without sacrificing defensibility.
          </p>
          <p className="text-xl font-semibold text-slate-900">
            Fragmentation erodes speed. Manual processing erodes confidence.
          </p>
        </div>

        {/* Problem grid cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          <ProblemCard
            title="Time wasted searching for information"
            body="Stop hunting across folders and tools, find the right answer fast, with evidence and context built in."
            icon={SearchIcon}
          />
          <ProblemCard
            title="Align around one trusted source of truth"
            body="Eliminate conflicting versions and ensure everyone works from the same verified information."
            icon={TruthIcon}
          />
          <ProblemCard
            title="Exchange structured data seamlessly across stakeholders"
            body="Share information across teams and systems without manual reformatting or data loss."
            icon={ExchangeIcon}
          />
          <ProblemCard
            title="Reduce manual handovers and errors"
            body="Automate information transfers between phases to minimize mistakes and delays."
            icon={HandoverIcon}
          />
          <ProblemCard
            title="Apply consistent governance across stakeholders"
            body="Enforce standards and compliance requirements uniformly across all project participants."
            icon={GovernanceIcon}
          />
          <ProblemCard
            title="Ensure continuity from delivery into long-term operations"
            body="Preserve project knowledge for operations teams long after construction is complete."
            icon={ContinuityIcon}
          />
        </div>

        {/* Built for Every Role section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
            Built for Every Role on the Project
          </h2>
          <p className="text-xl text-slate-700 max-w-2xl mx-auto">
            ARK adapts to the needs of different stakeholders while maintaining
            the same core promise: instant, trusted answers from your construction data.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {roles.map((role) => (
            <RoleCard key={role.label} {...role} />
          ))}
        </div>
      </div>
    </section>
  );
};
